import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useApolloClient, useMutation, useQuery, useSubscription } from '@apollo/client';
import config from '../../../config';
import {
  confirmInstallationPhotoUploadMutation,
  generateInstallationDocumentAsyncMutation,
  installationAccessCodesQuery,
  installationDocumentQuery,
  installationDocumentReadySubscription,
  installationPhotoUploadRequestQuery,
  installationPhotosQuery,
  installationQuery,
  issueInstallationAccessCodeMutation,
  removeInstallationPhotoMutation,
  revokeInstallationAccessCodeMutation,
  updateInstallationPhotoLabelMutation,
  updateInstallationSectionMutation,
} from '../../../graph/installation';
import {
  INSTALLATION_SECTIONS,
  SectionConfig,
  parseInstallationAnswers,
  sectionFieldsWithoutPhotos,
} from '../../../util/installationSections';

const useStyles = makeStyles(theme => ({
  nav: {
    [theme.breakpoints.up('md')]: {
      width: '18rem',
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    cursor: 'pointer',
    transition: 'background-color .15s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  navItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  navCheck: {
    color: theme.palette.success.main,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 3),
  },
  dropzone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color .15s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  thumbnail: {
    position: 'relative',
    width: theme.spacing(14),
    height: theme.spacing(14),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailDelete: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    padding: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
  },
  thumbnailLabel: {
    width: theme.spacing(14),
  },
  codes: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface Photo {
  fileName: string;
  url: string;
  label: string;
  isPdf: boolean;
}

interface AccessCode {
  id: number;
  name: string;
  code: string;
  createdAt: string;
  revokedAt?: string | null;
}

interface Props {
  hospital: any;
}

const HospitalInstallation: FC<Props> = ({ hospital }) => {
  const classes = useStyles();
  const client = useApolloClient();
  const organizationId = hospital?.id != null ? Number(hospital.id) : undefined;
  const variables = { organizationId };

  const [selectedId, setSelectedId] = useState(INSTALLATION_SECTIONS[0].id);
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [codeName, setCodeName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const saveTimer = useRef<number | null>(null);

  const { data, loading } = useQuery(installationQuery, { variables, skip: !organizationId, fetchPolicy: 'network-only' });
  const { data: photosData, refetch: refetchPhotos } = useQuery(installationPhotosQuery, {
    variables,
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });
  const { data: codesData, refetch: refetchCodes } = useQuery(installationAccessCodesQuery, {
    variables,
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });
  const { data: documentData, refetch: refetchDocument } = useQuery(installationDocumentQuery, {
    variables,
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });

  const { data: readyData } = useSubscription(installationDocumentReadySubscription, {
    variables,
    skip: !organizationId,
  });

  const [updateSection] = useMutation(updateInstallationSectionMutation);
  const [confirmPhoto] = useMutation(confirmInstallationPhotoUploadMutation);
  const [updatePhotoLabel] = useMutation(updateInstallationPhotoLabelMutation);
  const [removePhoto] = useMutation(removeInstallationPhotoMutation);
  const [issueCode, { loading: issuing }] = useMutation(issueInstallationAccessCodeMutation);
  const [revokeCode] = useMutation(revokeInstallationAccessCodeMutation);
  const [generateDocument] = useMutation(generateInstallationDocumentAsyncMutation);

  useEffect(() => {
    const answers = parseInstallationAnswers(data?.installation?.answers);
    const next: Record<string, Record<string, string>> = {};
    INSTALLATION_SECTIONS.forEach(section => {
      next[section.id] = sectionFieldsWithoutPhotos(answers[section.id]);
    });
    setValues(next);
  }, [data]);

  useEffect(() => {
    const result = readyData?.installationDocumentReady;
    if (!result) return;
    if (result.url) {
      setGenerating(false);
      setGenerateError(null);
      refetchDocument();
    } else if (result.error) {
      setGenerating(false);
      setGenerateError(result.error);
    }
  }, [readyData, refetchDocument]);

  useEffect(() => {
    if (!generating) return undefined;
    const timeout = window.setTimeout(() => {
      setGenerating(false);
      setGenerateError(current => current || 'Timed out waiting for the PDF. Refresh to check if it completed.');
    }, 120000);
    return () => window.clearTimeout(timeout);
  }, [generating]);

  const photosBySection = useMemo(() => {
    const grouped: Record<string, Photo[]> = {};
    (photosData?.installationPhotos || []).forEach((photo: any) => {
      const list = grouped[photo.section] || [];
      list.push({
        fileName: photo.fileName,
        url: photo.url,
        label: photo.label || '',
        isPdf: String(photo.fileName).toLowerCase().endsWith('.pdf'),
      });
      grouped[photo.section] = list;
    });
    return grouped;
  }, [photosData]);

  const selectedIndex = INSTALLATION_SECTIONS.findIndex(section => section.id === selectedId);
  const selected = INSTALLATION_SECTIONS[selectedIndex];
  const selectedPhotos = photosBySection[selected.id] ?? [];
  const accessCodes: AccessCode[] = codesData?.installationAccessCodes || [];
  const document = documentData?.installationDocument;
  const careUrl = `${config.careURL}/installation/${organizationId}`;

  const persistSection = useCallback(
    async (sectionId: string, fields: Record<string, string>) => {
      if (!organizationId) return;
      await updateSection({
        variables: { organizationId, section: sectionId, answers: JSON.stringify(fields) },
      });
    },
    [organizationId, updateSection],
  );

  const scheduleSave = (sectionId: string, fields: Record<string, string>) => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }
    saveTimer.current = window.setTimeout(() => {
      persistSection(sectionId, fields);
    }, 400);
  };

  const handleFieldChange = (sectionId: string, fieldKey: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValues(prev => {
      const nextSection = { ...prev[sectionId], [fieldKey]: value };
      const next = { ...prev, [sectionId]: nextSection };
      scheduleSave(sectionId, nextSection);
      return next;
    });
  };

  const uploadFiles = async (files: File[]) => {
    if (!organizationId || files.length === 0) return;
    const failed: string[] = [];
    for (const file of files) {
      try {
        const { data: requestData } = await client.query({
          query: installationPhotoUploadRequestQuery,
          variables: {
            organizationId,
            section: selected.id,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
          },
          fetchPolicy: 'network-only',
        });
        const upload = requestData?.installationPhotoUploadRequest;
        if (!upload?.url) throw new Error('No upload URL');
        const response = await fetch(upload.url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!response.ok) throw new Error(`Upload failed (${response.status})`);
        await confirmPhoto({
          variables: { organizationId, section: selected.id, fileName: upload.fileName, label: '' },
        });
      } catch {
        failed.push(file.name);
      }
    }
    setUploadErrors(failed);
    await refetchPhotos();
  };

  const handleAddPhotos = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    uploadFiles(files);
  };

  const handleRemovePhoto = async (fileName: string) => {
    if (!organizationId) return;
    await removePhoto({ variables: { organizationId, section: selected.id, fileName } });
    await refetchPhotos();
  };

  const handlePhotoLabelBlur = (fileName: string) => async (e: React.FocusEvent<HTMLInputElement>) => {
    if (!organizationId) return;
    await updatePhotoLabel({ variables: { organizationId, section: selected.id, fileName, label: e.target.value } });
    await refetchPhotos();
  };

  const sectionStarted = (section: SectionConfig) =>
    Object.values(values[section.id] ?? {}).some(value => !!value) || (photosBySection[section.id] ?? []).length > 0;

  const startedCount = INSTALLATION_SECTIONS.filter(sectionStarted).length;

  const handleGenerate = async () => {
    if (!organizationId) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      await generateDocument({ variables: { organizationId } });
    } catch (error: any) {
      setGenerating(false);
      setGenerateError(error?.message || 'Failed to generate document');
    }
  };

  const handleIssueCode = async () => {
    if (!organizationId || !codeName.trim()) return;
    await issueCode({ variables: { organizationId, name: codeName.trim() } });
    setCodeName('');
    await refetchCodes();
  };

  const handleRevoke = async (id: number) => {
    if (!organizationId) return;
    await revokeCode({ variables: { organizationId, id } });
    await refetchCodes();
  };

  if (loading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <Box className={classes.codes}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap="0.75rem">
          <Box>
            <Typography variant="subtitle1">Access codes</Typography>
            <Typography variant="body2" color="textSecondary">
              Installers open {careUrl} and enter an active code. Codes stay valid until revoked.
            </Typography>
            <Button size="small" onClick={() => navigator.clipboard.writeText(careUrl)}>
              Copy URL
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gridGap="0.5rem">
            <TextField
              size="small"
              label="Name (e.g. Duy — install team)"
              value={codeName}
              onChange={e => setCodeName(e.target.value)}
            />
            <Button variant="outlined" color="primary" disabled={issuing || !codeName.trim()} onClick={handleIssueCode}>
              Issue code
            </Button>
          </Box>
        </Box>
        <Box mt={2} display="flex" flexDirection="column" gridGap="0.5rem">
          {accessCodes.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No codes issued yet.
            </Typography>
          )}
          {accessCodes.map(code => (
            <Box key={code.id} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {code.name} — <strong>{code.code || '—'}</strong>{' '}
                {code.revokedAt ? (
                  <Chip size="small" label="Revoked" />
                ) : (
                  <Chip size="small" color="primary" label="Active" />
                )}
              </Typography>
              {!code.revokedAt && (
                <Button size="small" onClick={() => handleRevoke(code.id)}>
                  Revoke
                </Button>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        mb={2}
        px={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gridGap="0.75rem"
      >
        <Box display="flex" alignItems="center" gridGap="0.75rem">
          <Chip size="small" label={`${startedCount} / ${INSTALLATION_SECTIONS.length}`} />
          <Typography variant="body2" color="textSecondary">
            Capture installation details and photos for {hospital?.name ?? 'this organization'}. Everything is stored
            here and the acceptance document can be generated at any time.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gridGap="0.75rem">
          {document?.url && (
            <Button color="primary" href={document.url} target="_blank" rel="noopener noreferrer" startIcon={<Icon>picture_as_pdf</Icon>}>
              Download PDF
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={generating}
            onClick={handleGenerate}
            startIcon={generating ? <CircularProgress size={16} /> : <Icon>picture_as_pdf</Icon>}
          >
            {generating ? 'Generating…' : 'Generate Acceptance PDF'}
          </Button>
        </Box>
      </Box>
      {generateError && (
        <Typography variant="body2" color="error" gutterBottom>
          {generateError}
        </Typography>
      )}

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} border={1} borderColor="divider">
        <Box className={classes.nav} py={1}>
          {INSTALLATION_SECTIONS.map(section => (
            <Box
              key={section.id}
              className={clsx(classes.navItem, { [classes.navItemSelected]: section.id === selectedId })}
              onClick={() => {
                persistSection(selected.id, values[selected.id] || {});
                setSelectedId(section.id);
              }}
            >
              {sectionStarted(section) ? (
                <Icon className={classes.navCheck}>check_circle</Icon>
              ) : (
                <Icon color="disabled">radio_button_unchecked</Icon>
              )}
              <Typography variant="body2">{section.name}</Typography>
            </Box>
          ))}
        </Box>

        <Box className={classes.content}>
          <Typography variant="h3" gutterBottom>
            {selected.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {selected.description}
          </Typography>
          {selected.id === 'siteInformation' && (
            <Box mt={1} mb={2}>
              <Typography variant="body2">{hospital?.name}</Typography>
              <Typography variant="body2" color="textSecondary">
                {hospital?.address}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {hospital?.contact}
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Grid container spacing={2}>
              {selected.fields.map(field => (
                <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label}
                    type={field.type ?? 'text'}
                    multiline={field.multiline}
                    minRows={field.multiline ? 3 : undefined}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    value={values[selected.id]?.[field.key] ?? ''}
                    onChange={handleFieldChange(selected.id, field.key)}
                    onBlur={() => persistSection(selected.id, values[selected.id] || {})}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {selected.hasPhotos && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Photos
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Labels appear on the photos in the generated document (e.g. “PreOp 1”).
              </Typography>
              <Box display="flex" flexWrap="wrap" gridGap="0.75rem" alignItems="stretch">
                {selectedPhotos.map(photo => (
                  <Box key={photo.fileName} display="flex" flexDirection="column" gridGap="0.25rem">
                    <Box className={classes.thumbnail}>
                      {photo.isPdf ? (
                        <Tooltip title={photo.fileName}>
                          <Icon fontSize="large" color="primary">
                            picture_as_pdf
                          </Icon>
                        </Tooltip>
                      ) : (
                        <img src={photo.url} alt={photo.label || photo.fileName} className={classes.thumbnailImage} />
                      )}
                      <IconButton
                        size="small"
                        className={classes.thumbnailDelete}
                        onClick={() => handleRemovePhoto(photo.fileName)}
                      >
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Label (e.g. PreOp 1)"
                      defaultValue={photo.label}
                      onBlur={handlePhotoLabelBlur(photo.fileName)}
                      className={classes.thumbnailLabel}
                      inputProps={{ 'aria-label': 'Photo label' }}
                    />
                  </Box>
                ))}
                <Box
                  className={classes.dropzone}
                  flex={selectedPhotos.length === 0 ? 1 : undefined}
                  minWidth="10rem"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => fileInput.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    uploadFiles(Array.from(e.dataTransfer.files));
                  }}
                >
                  <Icon color="primary">add_a_photo</Icon>
                  <Typography variant="body2" color="textSecondary">
                    Drag photos here or click to upload
                  </Typography>
                </Box>
              </Box>
              {uploadErrors.length > 0 && (
                <Typography variant="body2" color="error">
                  Failed to upload: {uploadErrors.join(', ')}. Please try again.
                </Typography>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleAddPhotos}
              />
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              disabled={selectedIndex === 0}
              onClick={() => {
                persistSection(selected.id, values[selected.id] || {});
                setSelectedId(INSTALLATION_SECTIONS[selectedIndex - 1].id);
              }}
              startIcon={<Icon>chevron_left</Icon>}
            >
              Back
            </Button>
            <Button
              color="primary"
              disabled={selectedIndex === INSTALLATION_SECTIONS.length - 1}
              onClick={() => {
                persistSection(selected.id, values[selected.id] || {});
                setSelectedId(INSTALLATION_SECTIONS[selectedIndex + 1].id);
              }}
              endIcon={<Icon>chevron_right</Icon>}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HospitalInstallation;
